import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, Modal, Field, TextInput, TextArea, Select, StatusBadge } from "./adminShared";
import { formatINR, formatDate } from "@/lib/format";
import { Plus, Trash2, Pencil, Users } from "lucide-react";

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
// datetime-local helpers
const toLocal = (iso) => iso ? new Date(iso).toISOString().slice(0, 16) : "";
const fromLocal = (v) => v ? new Date(v).toISOString() : "";

export default function Workshops() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [regs, setRegs] = useState(null);

  const load = () => api.get("/admin/workshops").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ title: "", subtitle: "", description: "", isOnline: false, venue: { name: "", city: "Ludhiana" }, startDate: "", endDate: "", capacity: 12, confirmedCount: 0, price: 6500, status: "published", whoIsItFor: [], whatToBring: [], agenda: [] });

  const save = async () => {
    const payload = { ...edit, startDate: fromLocal(edit._start || toLocal(edit.startDate)), endDate: fromLocal(edit._end || toLocal(edit.endDate || edit.startDate)) };
    delete payload._start; delete payload._end; delete payload.seatsRemaining;
    try {
      if (edit.workshop_id) await api.put(`/admin/workshops/${edit.workshop_id}`, payload);
      else await api.post("/admin/workshops", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this workshop?")) return; await api.delete(`/admin/workshops/${id}`); toast.success("Deleted"); load(); };
  const viewRegs = async (w) => { const r = await api.get(`/admin/workshops/${w.workshop_id}/registrants`); setRegs({ w, list: r.data }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl text-earth" style={{ fontWeight: 600 }}>Workshops</h1>
        <button onClick={openNew} className="btn-gold text-sm" data-testid="new-workshop"><Plus size={16} className="mr-1" /> New</button>
      </div>
      {!items ? <p className="text-earth/50">Loading…</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((w) => (
            <Card key={w.workshop_id} className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <StatusBadge status={w.status} />
                  <h3 className="text-lg text-earth mt-2">{w.title}</h3>
                  <p className="text-sm text-earth/60">{formatDate(w.startDate)} · {w.isOnline ? "Online" : w.venue?.city} · {formatINR(w.price)}</p>
                  <p className="text-xs text-earth/40 mt-1">{w.confirmedCount}/{w.capacity} seats filled</p>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => viewRegs(w)} className="p-2 text-earth/50 hover:text-earth" title="Registrants"><Users size={16} /></button>
                  <button onClick={() => setEdit({ ...w, _start: toLocal(w.startDate), _end: toLocal(w.endDate) })} className="p-2 text-earth/50 hover:text-earth" data-testid={`edit-workshop-${w.slug}`}><Pencil size={16} /></button>
                  <button onClick={() => del(w.workshop_id)} className="p-2 text-earth/50 hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {edit && (
        <Modal title={edit.workshop_id ? "Edit workshop" : "New workshop"} onClose={() => setEdit(null)} wide>
          <Field label="Title"><TextInput value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="ws-title" /></Field>
          <Field label="Subtitle"><TextInput value={edit.subtitle} onChange={(e) => setEdit({ ...edit, subtitle: e.target.value })} /></Field>
          <Field label="Description"><TextArea rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Start (date & time)"><TextInput type="datetime-local" value={edit._start ?? toLocal(edit.startDate)} onChange={(e) => setEdit({ ...edit, _start: e.target.value })} data-testid="ws-start" /></Field>
            <Field label="End (date & time)"><TextInput type="datetime-local" value={edit._end ?? toLocal(edit.endDate)} onChange={(e) => setEdit({ ...edit, _end: e.target.value })} /></Field>
            <Field label="Capacity"><TextInput type="number" value={edit.capacity} onChange={(e) => setEdit({ ...edit, capacity: parseInt(e.target.value) || 0 })} /></Field>
            <Field label="Confirmed count"><TextInput type="number" value={edit.confirmedCount} onChange={(e) => setEdit({ ...edit, confirmedCount: parseInt(e.target.value) || 0 })} /></Field>
            <Field label="Price (₹)"><TextInput type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: parseInt(e.target.value) || 0 })} /></Field>
            <Field label="Online?"><Select value={edit.isOnline ? "yes" : "no"} onChange={(e) => setEdit({ ...edit, isOnline: e.target.value === "yes" })}><option value="no">In-person</option><option value="yes">Online</option></Select></Field>
          </div>
          {!edit.isOnline && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Venue name"><TextInput value={edit.venue?.name || ""} onChange={(e) => setEdit({ ...edit, venue: { ...edit.venue, name: e.target.value } })} /></Field>
              <Field label="City"><TextInput value={edit.venue?.city || ""} onChange={(e) => setEdit({ ...edit, venue: { ...edit.venue, city: e.target.value } })} /></Field>
            </div>
          )}
          <Field label="Who it's for (one per line)"><TextArea rows={2} value={toLines(edit.whoIsItFor)} onChange={(e) => setEdit({ ...edit, whoIsItFor: fromLines(e.target.value) })} /></Field>
          <Field label="What to bring (one per line)"><TextArea rows={2} value={toLines(edit.whatToBring)} onChange={(e) => setEdit({ ...edit, whatToBring: fromLines(e.target.value) })} /></Field>
          <Field label="Status"><Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}><option value="published">Published</option><option value="draft">Draft</option></Select></Field>
          <button onClick={save} className="btn-gold w-full" data-testid="ws-save">Save workshop</button>
        </Modal>
      )}

      {regs && (
        <Modal title={`Registrants — ${regs.w.title}`} onClose={() => setRegs(null)} wide>
          {regs.list.length === 0 ? <p className="text-earth/50">No registrants yet.</p> : (
            <div className="divide-y divide-hairline">
              {regs.list.map((r) => (
                <div key={r.registrationReference} className="py-3 flex justify-between">
                  <div><p className="font-semibold text-earth">{r.attendee?.fullName}</p><p className="text-sm text-earth/50">{r.attendee?.email} · {r.attendee?.phone} · {r.numberOfSeats} seat(s)</p></div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
